import {
  Component,
  HostListener,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnChanges,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AnalyticEvent, AnalyticsBloc } from '@kanalytics';
import { SearchBloc } from '@kp/search/search.bloc';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import {
  CardDisplayType,
  CardEventType,
  CardItemState,
  CardRequestType,
  CardScreen,
  CustomCardCategory,
} from '@ktypes/enums';
import { CardCollection, CardEvent, CardItem } from '@ktypes/models';
import isEqual from 'lodash/isEqual';
import { DragScrollComponent } from 'ngx-drag-scroll';
import { BehaviorSubject } from 'rxjs';

interface ViewportCardEvent {
  inViewport: boolean;
  card: CardItem;
}

@Component({
  selector: 'kp-card-category',
  templateUrl: './card-category.component.html',
  styleUrls: ['./card-category.component.scss'],
})
export class CardCategoryComponent implements OnInit, OnChanges {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _cardBloc: CardBloc,
    private _differs: KeyValueDiffers,
    private _searchBloc: SearchBloc
  ) {}

  @Input() cardCategoryIcon: string;
  @Input() cardCategoryLink: string;
  @Input() cardCollection: CardCollection;
  @Input() cardDisplayType: CardDisplayType;
  @Input() collectionIndex: number;
  @Input() customCardCategory: CustomCardCategory;
  @Input() categoryIndex: number;
  @Input() hasLocalResources: boolean;
  @Input() isLoading: boolean;
  @Input() isSearch = false;
  @Input() requestType: CardRequestType;
  @Input() screen: CardScreen;

  allCompleted = false;
  noReminders = false;
  card: CardItem;
  cardRequestType = CardRequestType;
  cardDisplayTypes = CardDisplayType;
  cardScreen = CardScreen;
  customCardCategories = CustomCardCategory;
  collectionCards: CardItem[];
  noCards = false;
  showLeftArrow = false;
  showRightArrow = false;

  @ViewChild('ds') private _ds: DragScrollComponent;

  // Needed for check of card collection changes
  private _cardCollectionDiffer: KeyValueDiffer<string, any>;

  // Needed for check of card.hideFromUI changes
  private _cardsDiffer: KeyValueDiffer<string, any>[] = [];

  // Needed for check of card user state changes
  private _cardUserStatesDiffer: KeyValueDiffer<string, any>[] = [];

  private _inViewportChange = new BehaviorSubject<ViewportCardEvent>(null);

  ngOnInit() {
    this._cardCollectionDiffer = this._differs.find(this.cardCollection?.cardItems ?? {}).create();
    this._checkCardStates();
  }

  ngOnChanges() {
    if (this.collectionCards?.length !== Object.values(this.cardCollection?.cardItems || {})?.length) {
      this._checkCardStates();
    }
    this._checkForChanges(this._cardCollectionDiffer?.diff(this.cardCollection?.cardItems));
    this.collectionCards?.map((cardItem) => {
      this._checkForChanges(this._cardsDiffer[this.collectionCards.indexOf(cardItem)]?.diff(cardItem));
      this._checkForChanges(
        this._cardUserStatesDiffer[this.collectionCards.indexOf(cardItem)]?.diff(cardItem.userState)
      );
    });
  }

  trackByCard(index: number, card: any): number {
    return card.id;
  }

  onInViewportChange(inViewport: boolean, card, itemIndex) {
    card.itemIndex = itemIndex;
    this._handleViewportChange({ inViewport, card });
    this._inViewportChange.next({ inViewport, card });
  }

  private _handleViewportChange(inViewportEvent: ViewportCardEvent) {
    if (!inViewportEvent?.card?.isInViewport && inViewportEvent?.inViewport) {
      //card has scrolled into viewport
      inViewportEvent.card.isInViewport = inViewportEvent.inViewport;
      if (!this.isSearch && (this.screen === CardScreen.discover || this.screen === CardScreen.recommended)) {
        this._cardBloc.handleCardEvents(
          new CardEvent(
            inViewportEvent.card.id,
            inViewportEvent.card,
            CardEventType.DISCOVER_VIEW,
            this.screen === CardScreen.discover ? CardRequestType.AVAILABLE : CardRequestType.RECOMMENDED
          )
        );
      }
      if (!this.isSearch && this.screen === CardScreen.reflect) {
        this._cardBloc.handleCardEvents(
          new CardEvent(
            inViewportEvent.card.id,
            inViewportEvent.card,
            CardEventType.REFLECT_VIEW,
            CardRequestType.REFLECTION
          )
        );
      }
      if (this.isSearch) {
        this._cardBloc.handleCardEvents(
          new CardEvent(
            inViewportEvent.card.id,
            inViewportEvent.card,
            CardEventType.SEARCH_VIEW,
            CardRequestType.SEARCH,
            {
              search_id: this._searchBloc.lastSearchId,
              search_type: this._searchBloc.lastSearchType,
              search_page: this.screen,
            }
          )
        );
      }
    } else if (inViewportEvent && inViewportEvent.card.isInViewport && !inViewportEvent.inViewport) {
      //card has left viewport
      inViewportEvent.card.isInViewport = inViewportEvent.inViewport;
    }
  }

  moveLeft() {
    this._ds.moveLeft();
  }

  moveRight() {
    this._ds.moveRight();
  }

  @HostListener('mousedown', ['$event'])
  onMousedown() {
    this._cardBloc.clickEnabled.next(true);
  }

  leftBoundStat(reachesLeftBound: boolean) {
    this.showLeftArrow = !reachesLeftBound;
    this._cardBloc.clickEnabled.next(false);
  }

  rightBoundStat(reachesRightBound: boolean) {
    this.showRightArrow = !reachesRightBound;
    this._cardBloc.clickEnabled.next(false);
  }

  private _checkCardStates(): void {
    this.collectionCards = Object.values(this.cardCollection?.cardItems || {});
    this.collectionCards?.map((cardItem) => {
      this._cardsDiffer.push(this._differs.find(cardItem ?? {}).create());
      this._cardUserStatesDiffer.push(this._differs.find(cardItem?.userState ?? {}).create());
    });
    this.noCards =
      this.collectionCards?.length === 0 ||
      this.collectionCards?.length ===
        this.collectionCards?.filter(
          (item: CardItem) => item?.userState?.currentState === CardItemState.ACT_REMOVED && item?.hideFromUI
        )?.length;
    this.allCompleted =
      !this.noCards &&
      this.collectionCards?.filter((item: CardItem) => (item ? !item?.userState?.isCompleted : null))?.length === 0;
    this.noReminders =
      this.collectionCards?.filter(
        (item: CardItem) => !item?.userState?.isCompleted && item.userState?.notifications?.length > 0
      )?.length === 0;
  }

  sendPageNavigationAnalyticEvent() {
    if (this.screen === CardScreen.recommended) {
      this._analyticsBloc.sendEvent(
        this._analyticsBloc.createEventFromEvent({ event: AnalyticEvent.discover_navigation_from_recommended })
      );
    }
  }

  private _checkForChanges(changes) {
    if (changes) {
      changes.forEachChangedItem((change) => {
        if (!isEqual(change.currentValue, change.previousValue)) {
          this._checkCardStates();
        }
      });
    }
  }
}
